import React from "react";
import {
  shallowEqual,
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from "react-redux";
import { useAppDispatch } from "redux/store";
import { IRootState } from "./reducer";

const EMPTY_ARGS = [];
export const useSelector: TypedUseSelectorHook<IRootState> = (selector) =>
  useReduxSelector(selector, shallowEqual);

export function useReload({
  action,
  actionArgs = EMPTY_ARGS,
  isReloadingEnabled,
  intervalMs,
  maxRetries = Infinity,
}) {
  const dispatch = useAppDispatch();
  const failureCountRef = React.useRef(0);

  const reloadOnce = React.useCallback(
    () => dispatch(action(...(actionArgs ?? []))),
    // accidentally passing actionArgs or action that is created on every render like actionArgs=[]
    // can lead to infinite immediate reloads
    [action, actionArgs]
  );

  React.useEffect(() => {
    if (!isReloadingEnabled) return;
    failureCountRef.current = 0;

    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const schedule = () => {
      timeoutId = setTimeout(async () => {
        if (cancelled) return;
        try {
          const result = await reloadOnce();
          if (!cancelled) {
            if ((result as any)?.meta?.requestStatus === "rejected") {
              failureCountRef.current += 1;
            } else {
              failureCountRef.current = 0;
            }
          }
        } catch {
          if (!cancelled) {
            failureCountRef.current += 1;
          }
        }
        if (!cancelled && failureCountRef.current < maxRetries) {
          schedule();
        }
      }, intervalMs);
    };

    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, isReloadingEnabled, reloadOnce, maxRetries]);

  return reloadOnce;
}

export const useParamSelector = (selector, ...params) =>
  useSelector((state: IRootState) => selector(state, ...params));
